"use client"

import { useState, useEffect } from "react"
import {
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
} from "@mui/material"
import walletSettingsService from "../../../services/WalletSettingsService"
import { toast } from "react-toastify"

export default function WalletSettingsConfig() {
  const [loading, setLoading] = useState(true)
  const [isEditId, setIsEditId] = useState(null)
  const [config, setConfig] = useState({
    categoryKey: "wallet_earning_rules",
    categoryName: "Wallet Points Earning Rules",
    settings: {
      earning_rules: {
        percentage_per_trip: 0,
        max_redemption_rate: {
          type: "fixed",
          value: 0,
        },
      },
    },
  })

  // Update percentage_per_trip
  const updatePercentagePerTrip = (value) => {
    if (value === "") {
      // Allow empty input for clearing the field
      setConfig((prevConfig) => ({
        ...prevConfig,
        settings: {
          ...prevConfig.settings,
          earning_rules: {
            ...prevConfig.settings.earning_rules,
            percentage_per_trip: "",
          },
        },
      }))
      return
    }
    
    const numValue = parseFloat(value)
    if (numValue < 0 || isNaN(numValue) || numValue > 100) return // Prevent negative values
    
    setConfig((prevConfig) => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        earning_rules: {
          ...prevConfig.settings.earning_rules,
          percentage_per_trip: numValue,
        },
      },
    }))
  }

  // Update max_redemption_rate type
  const updateRedemptionType = (type) => {
    setConfig((prevConfig) => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        earning_rules: {
          ...prevConfig.settings.earning_rules,
          max_redemption_rate: {
            ...prevConfig.settings.earning_rules.max_redemption_rate,
            type: type,
            value: "", // Reset value when type changes
          },
        },
      },
    }))
  }

  // Update max_redemption_rate value
  const updateRedemptionValue = (value) => {
    if (value === "") {
      // Allow empty input for clearing the field
      setConfig((prevConfig) => ({
        ...prevConfig,
        settings: {
          ...prevConfig.settings,
          earning_rules: {
            ...prevConfig.settings.earning_rules,
            max_redemption_rate: {
              ...prevConfig.settings.earning_rules.max_redemption_rate,
              value: "",
            },
          },
        },
      }))
      return
    }
    
    const numValue = parseFloat(value)
    if (numValue < 0 || isNaN(numValue)) return // Prevent negative values
    
    // For percentage type, restrict to max 100
    const isPercentage = config.settings.earning_rules.max_redemption_rate.type === "percentage"
    if (isPercentage && numValue > 100) return
    
    setConfig((prevConfig) => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        earning_rules: {
          ...prevConfig.settings.earning_rules,
          max_redemption_rate: {
            ...prevConfig.settings.earning_rules.max_redemption_rate,
            value: numValue,
          },
        },
      },
    }))
  }

  // Save configuration
  const saveConfiguration = async () => {
    try {
      const payload = {
        categoryKey: "wallet_earning_rules",
        categoryName: "Wallet Points Earning Rules",
        settings: config.settings,
      }

      let response
      if (isEditId) {
        response = await walletSettingsService.updateWalletSettings(isEditId, payload)
      } else {
        response = await walletSettingsService.createWalletSettings(payload)
      }

      console.log("Saved successfully:", response)
      toast.success("Wallet settings saved successfully!")
    } catch (error) {
      console.error("Error saving wallet settings:", error)
      toast.error(error.message || "Error saving wallet settings. Please try again.")
    }
  }

  // Reset configuration
  const handleReset = async () => {
    setLoading(true)
    try {
      const data = await walletSettingsService.getWalletSettings({
        categoryKey: "wallet_earning_rules",
      })

      if (data?.data?.[0]) {
        const settingsData = data.data[0]
        setConfig({
          categoryKey: "wallet_earning_rules",
          categoryName: "Wallet Points Earning Rules",
          settings: {
            earning_rules: {
              percentage_per_trip:
                settingsData.settings?.earning_rules?.percentage_per_trip || 0,
              max_redemption_rate: {
                type:
                  settingsData.settings?.earning_rules?.max_redemption_rate?.type || "fixed",
                value:
                  settingsData.settings?.earning_rules?.max_redemption_rate?.value || 0,
              },
            },
          },
        })
        setIsEditId(settingsData.id)
      }
    } catch (error) {
      toast.error(error.message || "Error resetting configuration.")
    } finally {
      setLoading(false)
    }
  }

  // Fetch settings on component mount
  useEffect(() => {
    const fetchWalletSettings = async () => {
      try {
        const data = await walletSettingsService.getWalletSettings({
          categoryKey: "wallet_earning_rules",
        })
        console.log("Fetched wallet settings:", data)

        if (data?.data?.[0]) {
          const settingsData = data.data[0]
          setConfig({
            categoryKey: "wallet_earning_rules",
            categoryName: "Wallet Points Earning Rules",
            settings: {
              earning_rules: {
                percentage_per_trip:
                  settingsData.settings?.earning_rules?.percentage_per_trip || 0,
                max_redemption_rate: {
                  type:
                    settingsData.settings?.earning_rules?.max_redemption_rate?.type || "fixed",
                  value:
                    settingsData.settings?.earning_rules?.max_redemption_rate?.value || 0,
                },
              },
            },
          })
          setIsEditId(settingsData.id)
        }
      } catch (error) {
        console.error("Error fetching wallet settings:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchWalletSettings()
  }, [])

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography>Loading...</Typography>
      </Box>
    )
  }

  return (
    <Card sx={{ margin: "auto", mt: 4 }}>
      <CardHeader
        title="Wallet Points Management"
        sx={{
          // backgroundColor: "primary.main",
          color: "black",
          "& .MuiCardHeader-title": {
            fontSize: "1.25rem",
            fontWeight: 600,
          },
        }}
      />
      <CardContent>
        <Grid container spacing={3}>
          {/* Percentage Per Trip */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Points Credit Rate
              </Typography>
              <TextField
                type="number"
                value={config.settings.earning_rules.percentage_per_trip}
                onChange={(e) => updatePercentagePerTrip(e.target.value)}
                placeholder="Enter percentage"
                fullWidth
                variant="outlined"
                size="medium"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, step: 0.1, max: 100 },
                }}
                helperText="Defines how much of the trip fare converts into points for the user upon trip completion."
              />
            </Box>
          </Grid>

          {/* Max Redemption Rate */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Maximum Redemption Limit
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Limit Type</FormLabel>
                <RadioGroup
                  row
                  value={config.settings.earning_rules.max_redemption_rate.type}
                  onChange={(e) => updateRedemptionType(e.target.value)}
                >
                  <FormControlLabel
                    value="fixed"
                    control={<Radio />}
                    label="Fixed Amount"
                  />
                  <FormControlLabel
                    value="percentage"
                    control={<Radio />}
                    label="Percentage"
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                type="number"
                value={config.settings.earning_rules.max_redemption_rate.value}
                onChange={(e) => updateRedemptionValue(e.target.value)}
                placeholder={
                  config.settings.earning_rules.max_redemption_rate.type === "fixed"
                    ? "Enter fixed amount"
                    : "Enter percentage"
                }
                fullWidth
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment:
                    config.settings.earning_rules.max_redemption_rate.type === "fixed" ? (
                      <InputAdornment position="start">₹</InputAdornment>
                    ) : null,
                  endAdornment:
                    config.settings.earning_rules.max_redemption_rate.type === "percentage" ? (
                      <InputAdornment position="end">%</InputAdornment>
                    ) : null,
                  inputProps: { 
                    min: 0, 
                    max: config.settings.earning_rules.max_redemption_rate.type === "percentage" ? 100 : undefined,
                    step: config.settings.earning_rules.max_redemption_rate.type === "percentage" ? 0.1 : 1 
                  },
                }}
                helperText={
                  config.settings.earning_rules.max_redemption_rate.type === "fixed"
                    ? "Defines how much of your trip cost can be covered using reward points."
                    : "Defines how much of your trip cost can be covered using reward points."
                }
              />
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
              <Button variant="outlined" onClick={handleReset}>
                Reset
              </Button>
              <Button variant="contained" onClick={saveConfiguration}>
                {!isEditId ? "Save Configuration" : "Update Configuration"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
